/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';

import { Environment, EnvironmentType } from '../../../../ts/assistant';
import { Release, ReleaseStatus } from '../../../../ts/release';
import { ReactivateSession } from '../../../../ts/session';
import { SkillType } from '../../../../ts/skill';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import * as releasesAPI from '../../api/releases';
import { getErrorString } from '../../util/errorHandler';
import {
  deletedObjectEvents,
  createdObjectEvents
} from '../../util/trackers/segment';
import { ONE_MINUTE } from '../config';
import { getEnvironment } from '../environments/actions';
import { selectEnvironmentById } from '../environments/selectors';
import { isCacheExpired } from '../helper';
import { addErrorMessage, addMessage, ToastStatus } from '../messages/actions';
import { getAssistantId } from '../session/selectors';
import { sleep } from '../skills/helper';
import { isProcessing } from './helper';
import { selectAllReleases, areReleasesLoading } from './selectors';

type ReleaseProps = {
  assistant_id: string;
  release: string;
};

/**
 * Formats a release and add fields like `has_dialog` or `is_live`.
 */
const formatRelease = (release: Release): Release => ({
  ...release,
  // FIXME: for next week's release, we will get rid of linked_assistant_references completely
  linked_assistant_references:
    release.linked_assistant_references || release.environment_references,
  is_live: release?.linked_assistant_references?.some(
    ref => ref.environment === EnvironmentType.LIVE
  ),
  has_dialog: release.content?.skills?.some(
    skill => skill.type === SkillType.DIALOG
  )
});

interface ListReleasesArgs {
  reactivateSession?: ReactivateSession;
}

export const listReleases = (args: ListReleasesArgs = {}) =>
  listReleasesWithArgs(args);

export const listReleasesWithArgs = createAsyncThunk<
  { assistantId: string; releases: Release[] },
  ListReleasesArgs,
  CreateAsyncThunk
>(
  'releases/list',
  async ({ reactivateSession }, { dispatch, getState }) => {
    try {
      const assistantId = getAssistantId(getState());
      const response = await releasesAPI.listReleases(
        assistantId,
        reactivateSession
      );
      const releases = response?.releases?.map(formatRelease);
      return {
        assistantId,
        releases
      };
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      return !areReleasesLoading(getState());
    }
  }
);

export const listReleasesIfRequired = (
  args: ListReleasesArgs = {}
): AsyncAppThunk<Promise<Release[]>> => {
  return async (dispatch, getState): Promise<Release[]> => {
    const { listFetched } = getState()?.releases;
    // Avoid requests if data is not stale
    if (!isCacheExpired(listFetched, ONE_MINUTE)) {
      return Promise.resolve(selectAllReleases(getState()));
    }
    await dispatch(listReleases(args));
    return selectAllReleases(getState());
  };
};

type CreateReleaseProps = {
  assistant_id: string;
  description: string;
  waitUntilCompleted?: boolean;
};

export const createRelease = createAsyncThunk<
  Release,
  CreateReleaseProps,
  CreateAsyncThunk
>('releases/create', async (params, { dispatch }) => {
  const assistantId = params.assistant_id;
  const waitUntilCompleted = params.waitUntilCompleted || false;
  try {
    const release = await releasesAPI.createRelease(
      assistantId,
      params.description
    );

    // Kick off a process to check on the release
    if (isProcessing(release) && waitUntilCompleted) {
      await dispatch(checkReleaseProgress(assistantId, release));
    } else if (isProcessing(release) && !waitUntilCompleted) {
      dispatch(checkReleaseProgress(assistantId, release));
    }

    createdObjectEvents.release({
      object: `${assistantId}--#${release.release}`,
      resultValue: 'success',
      successFlag: true
    });

    return formatRelease(release);
  } catch (error) {
    // Since we print the error message from Store as-is, let us replace
    // any mentions of snapshots with versions.
    if (error.response?.data?.error?.includes('snapshots limit exceeded')) {
      error.response.data.error =
        'Maximum versions exceeded: delete a version and try again.';
    }

    createdObjectEvents.release({
      object: `${assistantId}--`,
      resultValue: getErrorString(error),
      successFlag: false
    });
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const deleteRelease = createAsyncThunk<
  string,
  ReleaseProps,
  CreateAsyncThunk
>('releases/delete', async ({ assistant_id, release }, { dispatch }) => {
  try {
    await releasesAPI.deleteRelease(assistant_id, release);

    deletedObjectEvents.release({
      object: `${assistant_id}--#${release}`,
      resultValue: 'success',
      successFlag: true
    });

    return release;
  } catch (error) {
    deletedObjectEvents.release({
      object: `${assistant_id}--#${release}`,
      resultValue: getErrorString(error),
      successFlag: false
    });
    dispatch(addErrorMessage(error));
    throw error;
  }
});

type DeployReleaseProps = ReleaseProps & { environment: Environment };

export const deployRelease = createAsyncThunk<
  Environment,
  DeployReleaseProps,
  CreateAsyncThunk
>('releases/deploy', async (params, { dispatch, getState }) => {
  const { assistant_id, release, environment } = params;

  try {
    // Deploy API returns an assistant, eventually it will return an environment
    await releasesAPI.deployRelease(
      assistant_id,
      release,
      environment.environment_id,
      environment.environment
    );

    // FIXME: get the updated environment since store doesn't return it
    await dispatch(
      getEnvironment({
        assistantId: environment.assistant_id,
        environmentId: environment.environment_id
      })
    );
    return selectEnvironmentById(getState(), environment.environment_id);
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const checkReleaseProgress =
  (assistant_id: string, _release: Release): AsyncAppThunk<Promise<Release>> =>
  async dispatch => {
    let waitTimeInMs = 10000; // 10 seconds
    let release = _release;

    // Wait until the release is ready and dispatch updates in the meantime
    while (isProcessing(release)) {
      try {
        await sleep(waitTimeInMs);
        release = await dispatch(
          getRelease({
            assistant_id,
            release: release.release
          })
        ).unwrap();
      } catch (error) {
        // Wait a bit more if there is an error
        waitTimeInMs = 120000;
      }
    }

    // Notify the user if the release failed
    if (release.status === ReleaseStatus.FAILED) {
      await dispatch(
        addMessage({
          message: 'Error creating the release',
          status: ToastStatus.ERROR
        })
      );
    }

    return release;
  };

export const getRelease = createAsyncThunk<
  Release,
  ReleaseProps,
  CreateAsyncThunk
>('releases/get', async ({ assistant_id, release }, { dispatch }) => {
  try {
    const releaseResponse = await releasesAPI.getRelease(assistant_id, release);
    return formatRelease(releaseResponse);
  } catch (error) {
    await dispatch(addErrorMessage(error));
    throw error;
  }
});
